import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Navigation from "../components/Navigation";
import PortfolioSwitcher from "../components/PortfolioSwitcher";
import PortfolioOverviewGraph from "../components/PortfolioOverviewGraph";
import "./HomeScreen.css";

const formatUsd = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

const HomeScreenLoggedIn = () => {
  const { user, portfolioCurrency } = useAuth();
  const navigate = useNavigate();
  const [selectedPortfolio, setSelectedPortfolio] = useState("combined");
  const [portfolioValueUsd, setPortfolioValueUsd] = useState(0);
  const [periodChange, setPeriodChange] = useState({
    changeUsdInternal: 0,
    percentageChange: 0,
  });

  const handlePortfolioChange = (portfolioId) => {
    setSelectedPortfolio(portfolioId);
  };

  const handlePeriodChangeUpdate = useCallback((update) => {
    if (!update) return;
    setPeriodChange({
      changeUsdInternal: update.changeUsdInternal ?? 0,
      percentageChange: update.percentageChange ?? 0,
    });
  }, []);

  const portfolioId = selectedPortfolio === "combined" ? null : selectedPortfolio;
  const changeUsd = periodChange.changeUsdInternal ?? 0;
  const changePct = periodChange.percentageChange ?? 0;
  const changeClass =
    changeUsd > 0
      ? "portfolio-overview-card__change--up"
      : changeUsd < 0
        ? "portfolio-overview-card__change--down"
        : "portfolio-overview-card__change--flat";

  const portfolioAllocation = [
    { name: "Stocks", value: 65, color: "#3B82F6" },
    { name: "Crypto", value: 20, color: "#F59E0B" },
    { name: "Cash", value: 15, color: "#10B981" },
  ];

  return (
    <div className="screen">
      <div className="home-layout">
        <div className="portfolio-graph-section">
          <div className="scrollable-page-title">
            <h1>Home</h1>
          </div>

          <PortfolioSwitcher
            selectedPortfolio={selectedPortfolio}
            onPortfolioChange={handlePortfolioChange}
          />

          <div className="portfolio-overview-card">
            <div className="portfolio-overview-card__value-row">
              <span className="portfolio-overview-card__currency">
                {portfolioCurrency || "USD"}
              </span>
              <span className="portfolio-overview-card__amount">
                {formatUsd(portfolioValueUsd)}
              </span>
            </div>
            <div className={`portfolio-overview-card__change ${changeClass}`}>
              {changeUsd >= 0 ? "+" : ""}
              {formatUsd(changeUsd)} ({changePct >= 0 ? "+" : ""}
              {changePct.toFixed(2)}%)
            </div>

            <div className="portfolio-graph-container">
              <PortfolioOverviewGraph
                userId={user?.uid}
                portfolioId={portfolioId}
                timeFrame="1d"
                height={260}
                showTimeFramePicker={true}
                showZeroLine={true}
                exchangeRateFromUSD={1}
                onPortfolioValueUpdate={setPortfolioValueUsd}
                onPeriodChangeUpdate={handlePeriodChangeUpdate}
              />
            </div>
          </div>
        </div>

        <div className="portfolio-summary-section">
          <div className="summary-header">
            <h2>Portfolio Summary</h2>
            <button
              onClick={() => navigate("/invest")}
              className="btn btn-small"
            >
              Add Investment
            </button>
          </div>

          <div className="summary-card">
            <h3>Asset Allocation</h3>
            <div className="allocation-chart">
              <div className="pie-chart">
                {portfolioAllocation.map((asset, index) => (
                  <div
                    key={index}
                    className="pie-segment"
                    style={{
                      background: asset.color,
                      transform: `rotate(${index * 120}deg)`,
                      clipPath:
                        "polygon(50% 50%, 50% 0%, 100% 0%, 100% 100%, 50% 100%)",
                    }}
                  />
                ))}
              </div>
              <div className="allocation-legend">
                {portfolioAllocation.map((asset, index) => (
                  <div key={index} className="legend-item">
                    <div
                      className="legend-color"
                      style={{ backgroundColor: asset.color }}
                    />
                    <span className="legend-name">{asset.name}</span>
                    <span className="legend-value">{asset.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="summary-card">
            <h3>Quick Stats</h3>
            <div className="quick-stats">
              <div className="stat-card">
                <div className="stat-icon">💰</div>
                <div className="stat-content">
                  <div className="stat-title">Portfolio Value</div>
                  <div className="stat-amount">{formatUsd(portfolioValueUsd)}</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">📈</div>
                <div className="stat-content">
                  <div className="stat-title">Today&apos;s Change</div>
                  <div className="stat-amount">
                    {changeUsd >= 0 ? "+" : ""}
                    {formatUsd(changeUsd)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="summary-card">
            <h3>Quick Actions</h3>
            <div className="quick-actions">
              <button
                onClick={() => navigate("/market")}
                className="action-btn"
              >
                📊 View Market
              </button>
              <button
                onClick={() => navigate("/add-transaction")}
                className="action-btn"
              >
                ➕ Add Transaction
              </button>
              <button
                onClick={() => navigate("/transaction-history")}
                className="action-btn"
              >
                📋 History
              </button>
              <button
                onClick={() => navigate("/account")}
                className="action-btn"
              >
                ⚙️ Settings
              </button>
            </div>
          </div>
        </div>
      </div>
      <Navigation />
    </div>
  );
};

export default HomeScreenLoggedIn;
